// @ts-nocheck

import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { ButtonUI, InputSearch, ModalComponent, MultipleTagSelector, usePrevious } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { getInsightsConfig } from '@/store/selectors/reporting.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { ReportingInsightsItemsSelectionModalStyle } from './ReportingInsightsItemsSelectionModal.style';

const isIncluded = (text1, text2) => {
  return text1.match(new RegExp(text2, 'i'));
};

const hasUserPermission = (item, user, userRoles) => {
  return (
    !item.permissions ||
    (item.permissions &&
      item.permissions.every(permission => checkUserResourcePermissions(permission, user, userRoles)))
  );
};

function ReportingInsightsItemsSelectionModal({ filterCategories, isModalOpened, onClose, onSave, value }) {
  const { formatMessage } = useIntl();
  const { config } = useSelector(getInsightsConfig);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);

  const initialTagsConfig = config.reduce(
    (acc, next) => ({
      ...acc,
      [next.type]: [
        ...(acc[next.type] || []),
        ...next.items
          .filter(item => hasUserPermission(item, user, userRoles))
          .map(item => ({
            label: formatMessage({ id: item.title }),
            value: item.id
          }))
      ]
    }),
    {}
  );

  const initialFilters = filterCategories.map(f => ({
    ...f,
    items: initialTagsConfig[f.value]
  }));

  const prevInitialFilters = usePrevious(initialFilters);

  useEffect(() => {
    if (prevInitialFilters?.length !== initialFilters?.length) {
      setState(prevState => ({
        ...prevState,
        shownFilters: initialFilters
      }));
    }
  }, [initialFilters, prevInitialFilters]);

  const [{ isSaveButtonDisabled, selectedValues, shownFilters }, setState] = useState({
    isSaveButtonDisabled: true,
    selectedValues: value,
    shownFilters: initialFilters
  });

  useEffect(
    () =>
      setState(prevState => ({
        ...prevState,
        isSaveButtonDisabled: true,
        selectedValues: value
      })),
    [value]
  );

  const handleSearchChange = text => {
    let newShownFilters = initialFilters;
    if (text.length !== 0) {
      newShownFilters = initialFilters.reduce((acc, filter) => {
        let nextValue = [...acc];
        const categoryIncluded = isIncluded(filter.label, text);
        const itemsIncluded = filter.items.filter(item => isIncluded(item.label, text));
        if (categoryIncluded) {
          nextValue = [...acc, filter];
        } else if (itemsIncluded.length > 0) {
          nextValue = [
            ...acc,
            {
              ...filter,
              items: filter.items.filter(item => isIncluded(item.label, text))
            }
          ];
        }
        return nextValue;
      }, []);
    }
    setState(prevState => ({
      ...prevState,
      shownFilters: newShownFilters
    }));
  };

  const handleChange = (type, values) =>
    setState(prevState => {
      const newSelectedValues = cloneDeep(prevState.selectedValues);
      if (values.length) {
        newSelectedValues[type] = values;
      } else {
        delete newSelectedValues[type];
      }

      return {
        ...prevState,
        isSaveButtonDisabled: false,
        selectedValues: newSelectedValues
      };
    });

  const handleSave = () => {
    setState(prevState => ({
      ...prevState,
      shownFilters: initialFilters
    }));
    onSave?.(selectedValues);
  };

  const handleClose = () => {
    setState(prevState => ({
      ...prevState,
      isSaveButtonDisabled: true,
      shownFilters: initialFilters
    }));
    onClose?.();
  };

  return (
    <ModalComponent
      className="advancedFilterModal"
      description={formatMessage({
        defaultMessage: 'Set the content you want to view in the section',
        id: 'reporting.insights.filters.selectionModal.subTitle'
      })}
      isModalOpened={isModalOpened}
      onClose={handleClose}
      portalId="itemsSelection"
      theme="light"
      title={formatMessage({
        defaultMessage: 'Insights view settings',
        id: 'reporting.insights.filters.selectionModal.title'
      })}
    >
      <ModalComponent.ModalContent>
        <ReportingInsightsItemsSelectionModalStyle className="insightsSelectionModal">
          <InputSearch
            lens
            onChange={handleSearchChange}
            placeholder={formatMessage({
              defaultMessage: 'Search category by name or item name',
              id: 'reporting.insights.filters.selectionModal.searchPlaceholder'
            })}
          />
          {shownFilters.map(filter => (
            <div className="insightsSelectionModal__category" key={`insightsModalFilter_${filter.value}`}>
              <div className="insightsSelectionModal__categoryLabel">{filter.label}</div>
              <div className="insightsSelectionModal__categoryLabel">
                <MultipleTagSelector
                  items={filter.items}
                  mark
                  onChange={tagValue => handleChange(filter.value, tagValue)}
                  {...(value[filter.value] && { defaultSelectedItems: value[filter.value] })}
                />
              </div>
            </div>
          ))}
        </ReportingInsightsItemsSelectionModalStyle>
      </ModalComponent.ModalContent>
      <ModalComponent.CenterButton>
        <ButtonUI
          disabled={isSaveButtonDisabled}
          onClick={handleSave}
          text={formatMessage({
            defaultMessage: 'Save changes',
            id: 'reporting.advancedFilters.modal.saveChanges'
          })}
        />
      </ModalComponent.CenterButton>
    </ModalComponent>
  );
}

ReportingInsightsItemsSelectionModal.propTypes = {
  filterCategories: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string
    })
  ).isRequired,
  isModalOpened: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  value: PropTypes.any.isRequired
};

export default ReportingInsightsItemsSelectionModal;
